/**
 * Folder View Component
 *
 * Displays workspace items (tables, linked tables, folders) in a grid or list view.
 * Part of the Data Workspace OS feature set.
 *
 * Features:
 * - Grid and list view modes
 * - Sorting options
 * - Type filtering
 * - Empty state handling
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Table } from 'lucide-react';
import { ContextMenu, ContextMenuTrigger } from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';


import type { FolderViewProps, SortBy, SortDir } from './types';
import { FolderViewSkeleton } from './FolderViewSkeleton';
import { FolderViewHeader } from './FolderViewHeader';
import { FolderListView } from './FolderListView';
import { FolderGridView } from './FolderGridView';
import { StatusBar } from './StatusBar';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useSidePanelStore } from '../../stores/useSidePanelStore';
import { BackgroundContextMenu } from '../BackgroundContextMenu';

import { useUIStore } from '@/store/useUI';
import { EmptyView } from '@/components/common';

/**
 * Folder view for displaying workspace items
 */
export function FolderView({
  items,
  isLoading = false,
  folderId,
  folderName,
  isTrash = false,
  canEdit = true,
  onEmptyTrash,
  isEmptyingTrash = false,
  onCreateTable,
  onCreateLinkedTable,
  onCreateFolder,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
  onPreview,
  onResumeDraft,
}: FolderViewProps) {
  // View state - viewMode is persisted in the UI store
  const viewMode = useUIStore((state) => state.workspaceViewMode);
  const setViewMode = useUIStore((state) => state.setWorkspaceViewMode);
  const [sortBy, setSortBy] = useState<SortBy>('updated_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [typeFilter, setTypeFilter] = useState<WorkspaceItemType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selection store - clear on filter changes
  const clearSelection = useSelectionStore((state) => state.clearSelection);

  // Clear selection when filters change (to prevent orphaned selections)
  useEffect(() => {
    clearSelection();
  }, [typeFilter, searchQuery, clearSelection]);

  // Drag and drop (pass items to detect trash status)
  const {
    handleItemDragStart,
    handleItemDragEnd,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop,
    isDraggingItem,
    isDraggingOverFolder,
  } = useDragAndDrop({ items });

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let result = [...items];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(query);
        const descriptionMatch = item.description?.toLowerCase().includes(query) ?? false;
        return nameMatch || descriptionMatch;
      });
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      result = result.filter((item) => item.type === typeFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'updated_at':
          comparison =
            new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
          break;
        case 'created_at':
          comparison =
            new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
        case 'type':
          // Folders first, then tables, then linked tables
          const typeOrder = { folder: 0, table: 1, linked_table: 2 };
          comparison = typeOrder[a.type] - typeOrder[b.type];
          break;
      }

      return sortDir === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [items, searchQuery, typeFilter, sortBy, sortDir]);

  // Calculate total count (after type filter, before search)
  const totalCount = useMemo(() => {
    let count = items.length;
    if (typeFilter !== 'all') {
      count = items.filter((item) => item.type === typeFilter).length;
    }
    return count;
  }, [items, typeFilter]);

  // Keyboard navigation (uses filtered items)
  const { handleKeyDown } = useKeyboardNavigation({
    items: filteredAndSortedItems,
    viewMode,
    onPreview,
    onDelete,
  });

  // Handle sort change
  const handleSortChange = (newSortBy: SortBy) => {
    if (newSortBy === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDir('asc');
    }
  };

  // Selection state
  const selectedIds = useSelectionStore((state) => state.selectedIds);
  const selectAll = useSelectionStore((state) => state.selectAll);

  // Side panel state - close preview when selection changes
  const { isOpen: sidePanelOpen, mode: sidePanelMode, close: closeSidePanel } = useSidePanelStore();

  // Handle select all from context menu
  const handleSelectAll = useCallback(() => {
    const allIds = filteredAndSortedItems.map((item) => item.id);
    selectAll(allIds);
  }, [filteredAndSortedItems, selectAll]);

  // Track previous selection size to detect actual clearing (not initial empty state)
  const prevSelectionSizeRef = useRef(selectedIds.size);

  // Close preview panel when:
  // - Selection is cleared
  // - Multiple items are selected (multi-select)
  // - A folder is selected
  useEffect(() => {
    const prevSize = prevSelectionSizeRef.current;
    prevSelectionSizeRef.current = selectedIds.size;

    // Only affect preview modes, not createTable mode
    if (!sidePanelOpen || sidePanelMode === 'createTable') return;

    // Close if selection was CLEARED (previously had items, now empty)
    // This prevents closing when panel is opened via URL with no prior selection
    if (selectedIds.size === 0 && prevSize > 0) {
      closeSidePanel();
      return;
    }

    // Close if multiple items are selected (multi-select mode)
    if (selectedIds.size > 1) {
      closeSidePanel();
      return;
    }

    // If a folder is selected (check if selected item is a folder)
    // We need to check the type of the selected item
    const selectedId = Array.from(selectedIds)[0];
    const selectedItem = items.find((item) => item.id === selectedId);

    // If selected item is a folder, close the preview
    if (selectedItem?.type === 'folder') {
      closeSidePanel();
    }
  }, [selectedIds, items, sidePanelOpen, sidePanelMode, closeSidePanel]);

  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      // Check if click is on an item card/row by looking for data-workspace-item attribute
      const target = e.target as HTMLElement;
      const isItemClick = target.closest('[data-workspace-item]');

      // Only clear selection if clicking on empty space (not on an item)
      if (!isItemClick) {
        clearSelection();
      }
    },
    [clearSelection]
  );

  // Check if empty (for content area)
  const isEmpty = !isLoading && items.length === 0;
  const noResults = !isLoading && items.length > 0 && filteredAndSortedItems.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar - always shown */}
      <FolderViewHeader
        folderName={folderName}
        items={items}
        filteredCount={filteredAndSortedItems.length}
        totalCount={totalCount}
        viewMode={viewMode}
        sortBy={sortBy}
        sortDir={sortDir}
        typeFilter={typeFilter}
        searchQuery={searchQuery}
        isTrash={isTrash}
        isEmptyingTrash={isEmptyingTrash}
        canEdit={canEdit}
        onViewModeChange={setViewMode}
        onSortByChange={setSortBy}
        onSortDirChange={setSortDir}
        onTypeFilterChange={setTypeFilter}
        onSearchQueryChange={setSearchQuery}
        onCreateTable={onCreateTable}
        onCreateLinkedTable={onCreateLinkedTable}
        onCreateFolder={onCreateFolder}
        onEmptyTrash={onEmptyTrash}
      />

      {/* Content with right-click context menu */}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden" onClick={handleBackgroundClick}>
            {isLoading ? (
              <FolderViewSkeleton viewMode={viewMode} />
            ) : isEmpty ? (
              <div className="flex flex-1 h-full items-center justify-center">
                <EmptyView
                  title="No items yet"
                  description={
                    folderId
                      ? 'Right-click to create items in this folder.'
                      : 'Right-click to create your first table or linked table.'
                  }
                  icon={Table}
                />
              </div>
            ) : noResults ? (
              <div className="flex flex-1 h-full items-center justify-center">
                <EmptyView
                  title="No items match your search"
                  description={
                    searchQuery.trim()
                      ? 'Try adjusting your search terms or filters.'
                      : 'Try adjusting your filters.'
                  }
                  icon={Table}
                />
              </div>
            ) : viewMode === 'list' ? (
              <FolderListView
                items={filteredAndSortedItems}
                sortBy={sortBy}
                sortDir={sortDir}
                isTrash={isTrash}
                canEdit={canEdit}
                onKeyDown={handleKeyDown}
                onSortChange={handleSortChange}
                onDuplicate={onDuplicate}
                onToggleFavorite={onToggleFavorite}
                onMove={onMove}
                onDelete={onDelete}
                onRestore={onRestore}
                onDeletePermanently={onDeletePermanently}
                onPreview={onPreview}
                onResumeDraft={onResumeDraft}
                onDragStart={handleItemDragStart}
                onDragEnd={handleItemDragEnd}
                onDragOver={handleFolderDragOver}
                onDragLeave={handleFolderDragLeave}
                onDrop={handleFolderDrop}
                isDraggingItem={isDraggingItem}
                isDraggingOverFolder={isDraggingOverFolder}
              />
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <FolderGridView
                  items={filteredAndSortedItems}
                  viewMode={viewMode}
                  isTrash={isTrash}
                  canEdit={canEdit}
                  onKeyDown={handleKeyDown}
                  onDuplicate={onDuplicate}
                  onToggleFavorite={onToggleFavorite}
                  onMove={onMove}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onDeletePermanently={onDeletePermanently}
                  onPreview={onPreview}
                  onResumeDraft={onResumeDraft}
                  onDragStart={handleItemDragStart}
                  onDragEnd={handleItemDragEnd}
                  onDragOver={handleFolderDragOver}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={handleFolderDrop}
                  isDraggingItem={isDraggingItem}
                  isDraggingOverFolder={isDraggingOverFolder}
                />
              </div>
            )}
          </div>
        </ContextMenuTrigger>
        <BackgroundContextMenu
          canEdit={canEdit}
          onCreateTable={onCreateTable}
          onCreateLinkedTable={onCreateLinkedTable}
          onCreateFolder={onCreateFolder}
          onSelectAll={handleSelectAll}
        />
      </ContextMenu>

      {/* Status bar - shown when not loading and has items */}
      {!isLoading && items.length > 0 && (
        <StatusBar items={items} filteredCount={filteredAndSortedItems.length} />
      )}
    </div>
  );
}

// Re-export types
export type { WorkspaceItem, ViewMode, SortBy, SortDir, FolderViewProps } from './types';
